import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { getUsers, createUser, updateUser, deleteUser } from '../api';
import { useAuth } from '../context/AuthContext';

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'elder', label: 'Elder' },
  { value: 'ministry_leader', label: 'Ministry Leader' },
];

const ROLE_COLORS = {
  admin: 'error',
  elder: 'primary',
  ministry_leader: 'default',
};

function splitName(fullName) {
  const parts = (fullName || '').trim().split(/\s+/);
  return {
    first_name: parts[0] || '',
    last_name: parts.slice(1).join(' ') || '',
  };
}

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dialog, setDialog] = useState({ open: false, mode: 'add', item: null, first_name: '', last_name: '', email: '', role: 'ministry_leader' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [invitationSent, setInvitationSent] = useState(null);
  const { user: currentUser } = useAuth();

  const load = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleOpenAdd = () => {
    setDialog({ open: true, mode: 'add', item: null, first_name: '', last_name: '', email: '', role: 'ministry_leader' });
  };

  const handleOpenEdit = (u) => {
    const { first_name, last_name } = splitName(u.name);
    setDialog({ open: true, mode: 'edit', item: u, first_name, last_name, email: u.email, role: u.role });
  };

  const handleCloseDialog = () => setDialog({ ...dialog, open: false });

  const handleSave = async () => {
    try {
      const payload = {
        first_name: dialog.first_name.trim(),
        last_name: dialog.last_name.trim(),
        email: dialog.email.trim(),
        role: dialog.role,
      };

      if (dialog.mode === 'add') {
        const result = await createUser(payload);
        setInvitationSent({
          name: result.name,
          email: result.email,
        });
      } else {
        await updateUser(dialog.item.id, payload);
        setSnackbar({ open: true, message: 'User updated', severity: 'success' });
      }
      handleCloseDialog();
      load();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteUser(deleteConfirm.id);
      setSnackbar({ open: true, message: 'User deleted', severity: 'success' });
      setDeleteConfirm(null);
      load();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const roleLabel = (role) => ROLES.find(r => r.value === role)?.label || role;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Users</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>
          Add User
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5}>Loading...</TableCell></TableRow>
            ) : (
              users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>{u.name}</TableCell>
                  <TableCell>{u.username}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Chip label={roleLabel(u.role)} color={ROLE_COLORS[u.role] || 'default'} size="small" />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenEdit(u)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteConfirm(u)}
                      disabled={u.id === currentUser?.id}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add / Edit dialog */}
      <Dialog open={dialog.open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog.mode === 'add' ? 'Add User' : 'Edit User'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="First Name"
            value={dialog.first_name}
            onChange={(e) => setDialog({ ...dialog, first_name: e.target.value })}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            label="Last Name"
            value={dialog.last_name}
            onChange={(e) => setDialog({ ...dialog, last_name: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={dialog.email}
            onChange={(e) => setDialog({ ...dialog, email: e.target.value })}
            sx={{ mt: 2 }}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={dialog.role}
              label="Role"
              onChange={(e) => setDialog({ ...dialog, role: e.target.value })}
            >
              {ROLES.map((r) => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!dialog.first_name.trim() || !dialog.last_name.trim() || !dialog.email.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invitation sent confirmation */}
      <Dialog open={!!invitationSent} onClose={() => setInvitationSent(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Invitation Sent</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            An invitation email has been sent to <strong>{invitationSent?.email}</strong>
          </Alert>
          <Typography sx={{ mb: 2 }}>
            <strong>{invitationSent?.name}</strong> will receive an email with instructions to set up their password and access the portal.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The invitation link will expire in 48 hours. If it expires, the user can use "Forgot Password" on the login page to request a new link.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvitationSent(null)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          Are you sure you want to delete "{deleteConfirm?.name}"?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
